import React from "react";
import {
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Upload,
    Button,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;

interface Props {
    open: boolean;
    onClose: () => void;
    onSubmit?: (values: any) => void;
}

const CreateTaskModal: React.FC<Props> = ({
    open,
    onClose,
    onSubmit,
}) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();

    const handleFinish = (values: any) => {
        onSubmit?.(values);
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            title={
                <div>
                    <div className="text-lg font-semibold t-text-white">
                        {t('customer.create_task')}
                    </div>
                </div>
            }
            open={open}
            onCancel={() => {
                form.resetFields();
                onClose();
            }}
            footer={null}
            width={900}
            centered
            className="
                dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                dark:[&_.ant-modal-title]:text-white
                dark:[&_.ant-modal-close]:text-gray-300
            "
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
            >
                <div className="grid grid-cols-2 gap-x-6">

                    {/* TITLE */}
                    <Form.Item
                        label={<span>{t('customer.task_title')}</span>}
                        name="title"
                        rules={[{ required: true, message: t('customer.title_required') }]}
                        className="col-span-2"
                    >
                        <Input placeholder={t('customer.enter_value')} />
                    </Form.Item>

                    {/* DUE DATE */}
                    <Form.Item label={t('customer.due_date')} name="dueDate">
                        <DatePicker className="w-full" />
                    </Form.Item>

                    {/* STATUS */}
                    <Form.Item label={t('customer.status')} name="status">
                        <Select
                            placeholder={t('customer.select_status')}
                            options={[
                                { value: "open", label: t('customer.status_open') },
                                { value: "in_progress", label: t('customer.status_in_progress') },
                                { value: "done", label: t('customer.status_done') },
                            ]}
                        />
                    </Form.Item>

                    {/* ASSIGNEE */}
                    <Form.Item label={t('customer.assignee')} name="assignee">
                        <Select
                            placeholder={t('customer.select_assignee')}
                            options={[
                                { value: "huan", label: "PhanCao Huân" },
                                { value: "hai", label: "Nguyễn Hải" },
                            ]}
                        />
                    </Form.Item>

                    {/* FOLLOWERS */}
                    <Form.Item label={t('customer.followers')} name="followers">
                        <Select
                            mode="multiple"
                            placeholder={t('customer.select_followers')}
                            options={[
                                { value: "a", label: t('customer.person_a') },
                                { value: "b", label: t('customer.person_b') },
                            ]}
                        />
                    </Form.Item>

                    {/* CATEGORY */}
                    <Form.Item label={t('customer.category')} name="category">
                        <Select
                            placeholder={t('customer.select_category')}
                            options={[
                                { value: "call", label: t('customer.call') },
                                { value: "meeting", label: t('customer.meeting') },
                                { value: "follow_up", label: t('customer.follow_up') },
                            ]}
                        />
                    </Form.Item>

                    {/* PRIORITY */}
                    <Form.Item label={t('customer.priority')} name="priority">
                        <Select
                            placeholder={t('customer.select_priority')}
                            options={[
                                { value: "low", label: t('customer.low') },
                                { value: "medium", label: t('customer.medium') },
                                { value: "high", label: t('customer.high') },
                            ]}
                        />
                    </Form.Item>

                    {/* ATTACHMENT */}
                    <Form.Item label={t('customer.attachment')} name="attachment">
                        <Upload>
                            <Button icon={<UploadOutlined />}>
                                {t('customer.upload')}
                            </Button>
                        </Upload>
                    </Form.Item>

                    {/* DESCRIPTION */}
                    <Form.Item
                        label={t('customer.description')}
                        name="description"
                        className="col-span-2"
                    >
                        <TextArea rows={4} placeholder={t('customer.enter_description')} />
                    </Form.Item>
                </div>

                {/* FOOTER */}
                <div className="flex justify-end gap-3 mt-6">
                    <Button
                        onClick={() => {
                            form.resetFields();
                            onClose();
                        }}
                        className="dark:bg-[#2a2a2a] dark:border-[#303030] text-000000e0"
                    >
                        {t('customer.cancel')}
                    </Button>

                    <Button
                        htmlType="submit"
                        type="primary"
                        className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                    >
                        {t('customer.create')}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default CreateTaskModal;